import { useState, useMemo } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  Select,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Center,
  Loader,
  Divider,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
} from "@tabler/icons-react";
import {
  useReportDeliveryMethodsAdmin,
  useCreateReportDeliveryMethod,
  useUpdateReportDeliveryMethod,
  useToggleReportDeliveryMethodActive,
} from "../../../hooks/admin/useReportDeliveryMethods";
import ReportDeliveryMethodForm, {
  ReportDeliveryMethodFormValues,
} from "../../../components/admin/reportdeliverymethods/ReportDeliveryMethodForm";
import { ReportDeliveryMethod } from "../../../services/admin/reportdeliverymethods.service";

type StatusFilter = "all" | "active" | "inactive";
const ITEMS_PER_PAGE = 20;

export default function ReportDeliveryMethodsAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editingMethod, setEditingMethod] =
    useState<ReportDeliveryMethod | null>(null);
  const [togglingItem, setTogglingItem] = useState<ReportDeliveryMethod | null>(
    null
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const { data: methods = [], isLoading } = useReportDeliveryMethodsAdmin();
  const createMethod = useCreateReportDeliveryMethod();
  const updateMethod = useUpdateReportDeliveryMethod();
  const toggleActive = useToggleReportDeliveryMethodActive();

  // Filter methods by status and search
  const filteredMethods = useMemo(() => {
    let result = methods;

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(searchLower) ||
          m.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter((m) =>
        statusFilter === "active" ? m.isActive : !m.isActive
      );
    }

    return result;
  }, [methods, debouncedSearch, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredMethods.length / ITEMS_PER_PAGE);
  const paginatedMethods = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredMethods.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMethods, page]);

  const handleAdd = () => {
    setEditingMethod(null);
    openFormModal();
  };

  const handleEdit = (item: ReportDeliveryMethod) => {
    setEditingMethod(item);
    openFormModal();
  };

  const handleSubmit = async (values: ReportDeliveryMethodFormValues) => {
    if (editingMethod) {
      await updateMethod.mutateAsync({
        id: editingMethod.id,
        ...values,
      });
    } else {
      await createMethod.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (item: ReportDeliveryMethod) => {
    setTogglingItem(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (!togglingItem) return;
    try {
      await toggleActive.mutateAsync({
        id: togglingItem.id,
        isActive: !togglingItem.isActive,
      });
      closeConfirmModal();
      setTogglingItem(null);
    } catch {
      // Error is handled by the mutation's onError callback
      // Just close the modal
      closeConfirmModal();
      setTogglingItem(null);
    }
  };

  return (
    <Container fluid py="xl">
      <Stack gap="lg" maw={1200} mx="auto">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Report Delivery Methods</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Manage report delivery methods (Fax, Email, Portal, etc.)
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
            Add Method
          </Button>
        </Group>

        {/* Search and Filters Row */}
        <Group align="stretch" grow>
          {/* Search Section */}
          <Paper p="md" withBorder style={{ flex: 2 }}>
            <Stack gap="sm">
              <Group>
                <IconSearch size={20} />
                <Text fw={500}>Search</Text>
              </Group>
              <TextInput
                placeholder="Search by name or description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
              />
            </Stack>
          </Paper>

          {/* Filters Section */}
          <Paper p="md" withBorder style={{ flex: 1 }}>
            <Stack gap="sm">
              <Group>
                <IconFilter size={20} />
                <Text fw={500}>Filters</Text>
              </Group>
              <Select
                placeholder="Filter by status"
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter((value as StatusFilter) || "all");
                  setPage(1);
                }}
                data={[
                  { label: "All Status", value: "all" },
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                ]}
              />
              <Text size="sm" c="dimmed">
                {filteredMethods.length} method(s) found
              </Text>
            </Stack>
          </Paper>
        </Group>

        {/* Table Section */}
        <Paper p="md" withBorder>
          <Stack gap="md">
            {/* Table Header */}
            <Group justify="space-between">
              <Text fw={500} size="lg">
                Methods ({filteredMethods.length})
              </Text>
              {totalPages > 1 && (
                <Group gap="md">
                  <Text size="sm" c="dimmed">
                    Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                    {Math.min(page * ITEMS_PER_PAGE, filteredMethods.length)} of{" "}
                    {filteredMethods.length}
                  </Text>
                  <Pagination
                    total={totalPages}
                    value={page}
                    onChange={setPage}
                    size="sm"
                  />
                </Group>
              )}
            </Group>

            {/* Table */}
            {isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : filteredMethods.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                {debouncedSearch || statusFilter !== "all"
                  ? "No methods match the current filters"
                  : "No report delivery methods found"}
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Method Name</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th style={{ width: 100 }}>Status</Table.Th>
                    <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedMethods.map((method) => (
                    <Table.Tr
                      key={method.id}
                      style={{ opacity: method.isActive ? 1 : 0.5 }}
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {method.name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>
                          {method.description || "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={method.isActive ? "green" : "gray"}
                          variant="light"
                          size="sm"
                        >
                          {method.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Tooltip label="Edit">
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              onClick={() => handleEdit(method)}
                            >
                              <IconEdit size={18} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip
                            label={method.isActive ? "Deactivate" : "Activate"}
                          >
                            <ActionIcon
                              variant="subtle"
                              color={method.isActive ? "red" : "green"}
                              onClick={() => handleToggleActive(method)}
                            >
                              {method.isActive ? (
                                <IconX size={18} />
                              ) : (
                                <IconCheck size={18} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Add/Edit Modal */}
      <Modal.Root
        opened={formModalOpened}
        onClose={closeFormModal}
        size="md"
        centered
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              {editingMethod
                ? "Edit Report Delivery Method"
                : "Add New Report Delivery Method"}
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <ReportDeliveryMethodForm
              initialValues={editingMethod || undefined}
              onSubmit={handleSubmit}
            />
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={closeFormModal}>
              Cancel
            </Button>
            <Button
              loading={createMethod.isPending || updateMethod.isPending}
              onClick={() => {
                const form = document.querySelector("form");
                if (form) form.requestSubmit();
              }}
            >
              {editingMethod ? "Update" : "Create"}
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>

      {/* Confirmation Modal for Activate/Deactivate */}
      <Modal.Root
        opened={confirmModalOpened}
        onClose={() => {
          closeConfirmModal();
          setTogglingItem(null);
        }}
        size="sm"
        centered
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              {togglingItem?.isActive
                ? "Deactivate Delivery Method"
                : "Activate Delivery Method"}
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Text>
              Are you sure you want to{" "}
              <Text span fw={600}>
                {togglingItem?.isActive ? "deactivate" : "activate"}
              </Text>{" "}
              the delivery method{" "}
              <Text span fw={600}>
                &quot;{togglingItem?.name}&quot;
              </Text>
              ?
            </Text>
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button
              variant="default"
              onClick={() => {
                closeConfirmModal();
                setTogglingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color={togglingItem?.isActive ? "red" : "green"}
              loading={toggleActive.isPending}
              onClick={confirmToggleActive}
            >
              {togglingItem?.isActive ? "Deactivate" : "Activate"}
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </Container>
  );
}
